//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from '../common/service';
import ProviderVisitsModel from './providerVisits.model';
import productPlacementsModel from './productPlacements.model';

/**
 * @service ProductPlacementsService
 * @description Service for managing product placement and movement data within the Trading Community module.
 * This service handles the retrieval of records related to products placed at customer sites, products returned,
 * and product movements during provider visits.
 *
 * It supports various hierarchical views (Business, Manager, Technician) and date-based filtering to generate
 * reports on product flow and inventory placement.
 *
 * **Key Responsibilities:**
 * 1. **Placement Tracking**: Fetching details of products placed during specific visits.
 * 2. **Return Management**: Retrieving records of returned products.
 * 3. **Hierarchical Reporting**: Filtering data based on the organizational level (Business vs. Manager vs. Technician).
 * 4. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * @feature 1. Retrieve product placement details by visit ID.
 * @feature 2. Generate reports on placed products within a date range.
 * @feature 3. Generate reports on returned products within a date range.
 * @feature 4. Filter placements by specific technician.
 *
 * @architecture
 * Extends base Service<productPlacementsModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /blubay/productplacements/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})

export class ProductPlacementsService extends Service<productPlacementsModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/blubay/productplacements/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(httpClient: HttpClient) {
    super(httpClient, '/blubay/productplacements/');
  }

  /**
   * @method getProductPlacementDeatilsById
   * @async
   * @description Fetch detailed product placement information for a specific visit.
   *
   * Includes related data such as product name, quantity, UOM, and price per placed quantity.
   *
   * @param {string} visit_id - The unique identifier of the provider visit.
   * @returns {Promise<Array<ProviderVisitsModel>>} Promise resolving to an array of visit models containing placement details.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getProductPlacementDeatilsById(visit_id: string): Promise<Array<ProviderVisitsModel>> {
    return new Promise<Array<ProviderVisitsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ProviderVisitsModel>>(`${this.baseUrl}${this.serviceUrl}?visit_id=${visit_id}&include=ProductPlacementDetailsModel.product_name,qty,product_uom_name,price_per_placed_qty`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllProductsPlaced
   * @async
   * @description Fetch all products placed within a specific date range.
   *
   * Filters records where `product=placed`.
   *
   * @param {string} from_date - Start date of the range (YYYY-MM-DD).
   * @param {string} to_date - End date of the range (YYYY-MM-DD).
   * @returns {Promise<Array<ProviderVisitsModel>>} Promise resolving to the list of placed products.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getAllProductsPlaced(from_date: string, to_date: string): Promise<Array<ProviderVisitsModel>> {
    return new Promise<Array<ProviderVisitsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ProviderVisitsModel>>(`${this.baseUrl}${this.serviceUrl}productsPlaced?product=placed&from_date=${from_date}&to_date=${to_date}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllProductReturnedByBusinessLevel
   * @async
   * @description Fetch all products returned within a specific date range at the business level.
   *
   * Filters records where `product=returned`.
   *
   * @param {string} from_date - Start date of the range.
   * @param {string} to_date - End date of the range.
   * @returns {Promise<Array<ProviderVisitsModel>>} Promise resolving to the list of returned products.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getAllProductReturnedByBusinessLevel(from_date: string, to_date: string): Promise<Array<ProviderVisitsModel>> {
    return new Promise<Array<ProviderVisitsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ProviderVisitsModel>>(`${this.baseUrl}${this.serviceUrl}productsPlaced?product=returned&from_date=${from_date}&to_date=${to_date}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllProductReturnedByManagerLevel
   * @async
   * @description Fetch returned products filtered by specific visiting personnel (Managers/Technicians).
   *
   * @param {string} visiting_person_ids - Comma-separated list of person IDs.
   * @param {string} from_date - Start date of the range.
   * @param {string} to_date - End date of the range.
   * @returns {Promise<Array<ProviderVisitsModel>>} Promise resolving to the filtered list of returned products.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getAllProductReturnedByManagerLevel(visiting_person_ids: string, from_date: string, to_date: string): Promise<Array<ProviderVisitsModel>> {
    return new Promise<Array<ProviderVisitsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ProviderVisitsModel>>(`${this.baseUrl}${this.serviceUrl}productsPlaced?product=returned&visiting_person_id=${visiting_person_ids}&from_date=${from_date}&to_date=${to_date}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllProductsPlacedByTechnicianID
   * @async
   * @description Fetch all products placed by a specific technician.
   *
   * @param {string} placement_by_person_id - The unique identifier of the technician who placed the products.
   * @returns {Promise<Array<ProviderVisitsModel>>} Promise resolving to the list of placements by the technician.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getAllProductsPlacedByTechnicianID(placement_by_person_id: string): Promise<Array<ProviderVisitsModel>> {
    return new Promise<Array<ProviderVisitsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ProviderVisitsModel>>(`${this.baseUrl}${this.serviceUrl}?placement_by_person_id=${placement_by_person_id}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
